//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { getAddress } from 'ethers'
import { EXCHANGE_RATE_ONE, PoolRolloverActor, RoleEnum } from './Enums'
import { logOT } from './loggingShared'
import { EthAddr } from './SchemaTypes'

export type APR = `${number}`
export type ERC20Wei = `${number}`
export type ERC20DecString = `${number}`
export type ERC20CommaDecString = string

export type EthDate = number

//
// function isDigitsOnlyString (str: string): assert str is MWEI {
//   return /^\d+$/.test(str)
// }
export function toRoot365(value: number): number {
  const rootNumber = 1 + value / 10000

  const dailyRate = Math.pow(rootNumber, 1 / 365) - 1

  return Math.round(dailyRate * 1e18)
}

const BIG_RAY = 1_000_000_000_000n
const BIG_RAY_MINUS_1 = BIG_RAY - 1n

const NUMBER_RAY = Number(BIG_RAY)

export const assetsAndSharesToExchangeRate = (assets: number, shares: string | number): bigint => {
  logOT.debug('assetsAndSharesToExchangeRate', assets, shares)
  const er = (EXCHANGE_RATE_ONE * BigInt(assets * 1e6 * NUMBER_RAY)) / BigInt(shares) / BIG_RAY
  return er
}

export function toAnnualLinerReturn(exchangeRate: number | string, exchangeRateChangeRate: number | string): number {
  return (
    (Number(((BigInt(exchangeRate) + BigInt(exchangeRateChangeRate) * 365n) * BIG_RAY) / BigInt(exchangeRate)) -
      NUMBER_RAY) /
    NUMBER_RAY
  )
}
export function isNumber(input: string): boolean {
  // Check if the string is not empty and is a valid number
  return input.trim() !== '' && !isNaN(Number(input))
}

export function exchangeRateCompounded1Year(
  exchangeRate: number | string,
  exchangeRateCompoundingRate: number | string
): string {
  let endingCompoundingExhcahngeRate = BigInt(exchangeRate)
  for (let i = 0; i < 365; i++) {
    endingCompoundingExhcahngeRate =
      (endingCompoundingExhcahngeRate * BigInt(exchangeRateCompoundingRate)) / EXCHANGE_RATE_ONE
  }
  return endingCompoundingExhcahngeRate.toString()
}

export function annualCompoundingReturn(
  exchangeRate: number | string,
  exchangeRateCompoundingRate: number | string
): number {
  const endingCompoundingExhcahngeRate = exchangeRateCompounded1Year(exchangeRate, exchangeRateCompoundingRate)
  const annualCompundingReturn =
    (Number((BigInt(endingCompoundingExhcahngeRate) * BIG_RAY) / BigInt(exchangeRate)) - NUMBER_RAY) / NUMBER_RAY

  logOT.debug('compounding rate', exchangeRateCompoundingRate, endingCompoundingExhcahngeRate, annualCompundingReturn)
  return annualCompundingReturn
}
export function annualReturnToExhchangeRateCompoundingRate(alr: number): string {
  const singleDateReturn = Math.pow(1 + alr, 1 / 365)
  logOT.debug('singleDateReturn', alr, singleDateReturn)
  const erc = (BigInt(Math.round(singleDateReturn * NUMBER_RAY)) * EXCHANGE_RATE_ONE) / BIG_RAY
  return erc.toString()
}

export function annualReturnToExhchangeRateChangeRate(alr: number, exchangeRate: bigint): string {
  const erc = ((BigInt(alr * NUMBER_RAY) * exchangeRate) / 365n + BIG_RAY_MINUS_1) / BIG_RAY
  return erc.toString()
}
export const exchangeRateToDecimalString = (value: string | bigint): string => {
  const bigIntValue = typeof value === 'bigint' ? value : BigInt(value)

  const integerPart = bigIntValue / EXCHANGE_RATE_ONE
  const fractionalPart = bigIntValue % EXCHANGE_RATE_ONE

  // Format the fractional part as a zero-padded string
  const fractionalPartStr = fractionalPart.toString().padStart(18, '0')

  return `${integerPart}.${fractionalPartStr}`
}

export const exchangeRateDecimalStringToExchangeRate = (decimalStr: string): bigint => {
  const [integerPart, fractionalPart = ''] = decimalStr.split('.')
  const normalizedFractionalPart = fractionalPart.padEnd(18, '0').slice(0, 18)
  logOT.debug('normalizedFractionalPart', normalizedFractionalPart.length)
  logOT.debug('integerPart', integerPart)
  const combinedStr = integerPart + normalizedFractionalPart

  return BigInt(combinedStr)
}

export const exchangeRateMaturityDayCalc = (exchangeRate: bigint | string, apr: number, numDays: number): bigint => {
  const eram =
    BigInt(exchangeRate) + (BigInt(exchangeRate) * BigInt(apr * NUMBER_RAY) * BigInt(numDays)) / 365n / BIG_RAY
  return eram
}
export function otAssert(condition: any, description?: string): asserts condition {
  if (!condition) {
    if (description) {
      throw new Error('Asserting Failed: ' + description)
    }
    throw new Error('Assertion failed')
  }
}
export const dayNumberToDateString = (dayNumber: number): String => {
  if (!dayNumber) {
    return toUTCDateString(new Date().getTime() / 1000)
  }
  return toUTCDateString(dayNumber * 24 * 60 * 60)
}
export const dayNumberToDate = (dayNumber: number): Date => {
  if (!dayNumber) {
    new Date()
  }
  return new Date(dayNumber * 24 * 60 * 60 * 1000)
}
export const toUTCDateString = (timestamp: EthDate): string => {
  const date = new Date(timestamp * 1000)

  const year = date.getUTCFullYear()
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  const day = String(date.getUTCDate()).padStart(2, '0')
  return `${day}-${month}-${year} `
}

export const toLongUTCDateString = (timestamp: EthDate): string => {
  const date = new Date(timestamp * 1000)

  const year = date.getUTCFullYear()
  const month = date.toLocaleString('default', { month: 'long' })

  const day = String(date.getUTCDate()).padStart(2, '0')
  return `${day} ${month}, ${year} `
}

export const toUTCTimeString = (timestamp: EthDate): string => {
  const date = new Date(timestamp * 1000)
  const hours = String(date.getUTCHours()).padStart(2, '0')
  const minutes = String(date.getUTCMinutes()).padStart(2, '0')

  return `${hours}:${minutes} UTC`
}
export const ethValueDisplay = (value: string): string => {
  return value.slice(0, 5) + '...' + value.slice(-2)
}
export const rolloverActorToRoleEnum = (actor: number): RoleEnum => {
  // eslint-disable-next-line no-nested-ternary
  // prettier-ignore
  return actor === PoolRolloverActor.PoolAdmin
    ? RoleEnum.POOL_ADMIN
    : actor === PoolRolloverActor.BorrowerManager
    ? RoleEnum.BORROWER
    : actor === PoolRolloverActor.Investor
    ? RoleEnum.LENDER
    : actor === PoolRolloverActor.BorrowerWallet
    ? RoleEnum.BORROWER_WALLET
    : RoleEnum.NONE
}
export const toHourString = (seconds: number): string => {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  return `${h}h:${m}m`
}
export const Erc20Wei = (value: string | bigint | number): ERC20Wei => {
  let v: string
  if (typeof value == 'bigint') {
    if (value < 0n) {
      const e = new Error('Erc20Wei BigNumber Value less than zero rounding to zero')

      logOT.error('Erc20Wei BigNumber Value less than zero rounding to zero ', value, e.stack)

      value = 0n
    }
    v = value.toString()
  } else if (typeof value === 'number') {
    v = value.toString()
  } else {
    v = value
  }
  const regex = /^\d+$/
  if (regex.test(v)) {
    return v as ERC20Wei
  }
  throw new Error(`Value not Mwei ${v}`)
}
export const Erc20WeiNumber = (value: string | bigint): number => {
  return Number(Erc20Wei(value))
}
export const weiToNumber = (wei: string | bigint | number): number => {
  if (wei === undefined) {
    return 0
  }
  if (typeof wei === 'number') {
    return erc20WeiToNumber(Math.floor(wei).toString() as ERC20Wei)
  }
  if (typeof wei === 'bigint') {
    return erc20WeiToNumber(wei.toString() as ERC20Wei)
  }
  return erc20WeiToNumber(wei as ERC20Wei)
}

//
// const fromWei = (value: ERC20Wei, unit: string): ERC20DecString => {
//   if (unit === 'mwei') {
//     return BigInt(value).shiftedBy(-6).toString() as ERC20DecString
//   }
//   throw new Error(`Unknown unit ${unit}`)
// }

// const toWei = (value: ERC20DecString | string, unit: string): ERC20Wei => {
//   if (unit === 'mwei') {
//     logOTShared.debug('toWei', value)
//     return BigInt(Number(value)) * BigInt(10).pow(6)).toString() as ERC20Wei
//   }
//   throw new Error(`Unknown unit ${unit}`)
// }

export const pptToUSDC = (ppt: ERC20Wei, exchangeRate: ERC20Wei): ERC20Wei => {
  const usdc = (BigInt(ppt) * BigInt(exchangeRate)) / BigInt(1e6) / BigInt(1e12)

  return usdc.toString() as ERC20Wei
}

export const toMweiErc20Wei = (value: ERC20DecString): ERC20Wei => {
  const locale = getLocale()
  const formatter = new Intl.NumberFormat(locale)
  const formatParts = formatter.formatToParts(12345.6) // Example number to detect delimiters

  const decimalSeparator = formatParts.find((p) => p.type === 'decimal')?.value || '.'
  const groupSeparator = formatParts.find((p) => p.type === 'group')?.value || ','

  if (!value.includes(decimalSeparator)) {
    const integerPart = value.replace(groupSeparator, '')
    return (integerPart + '000000') as ERC20Wei
  }
  const parts = value.split(decimalSeparator)
  const integerPart = parts[0].replace(groupSeparator, '')
  let decimalPart = parts[1] || ''

  // Pad or truncate the decimal part to have exactly 6 digits
  decimalPart = decimalPart.padEnd(6, '0').slice(0, 6)

  // Combine the integer and decimal parts with a decimal point
  const result = integerPart + decimalPart

  return result as ERC20Wei
}

export const toErc20Wei = (value: ERC20DecString | number): ERC20Wei => {
  if (typeof value === 'number') {
    return toMweiErc20Wei(value.toString() as ERC20DecString)
  }
  return toMweiErc20Wei(value)
}
export const erc20WeiToCommaDecString = (wei: ERC20Wei | bigint | number | undefined): string => {
  const locale = getLocale()
  if (wei === undefined) {
    return '0'
  }
  let numberValue: number = 0
  if (typeof wei === 'number') {
    numberValue = erc20WeiToNumber(Math.floor(wei).toString() as ERC20Wei)
  } else if (typeof wei === 'bigint' || typeof wei === 'object') {
    numberValue = erc20WeiToNumber(wei.toString() as ERC20Wei)
  } else {
    numberValue = erc20WeiToNumber(wei as ERC20Wei)
  }

  const flooredValue = Math.round(numberValue * 100) / 100

  return flooredValue.toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}
export const fromWeiToMWeiERC20NumberString = (value: ERC20Wei): ERC20DecString => {
  const valueString = value.toString()
  const stripLeadingZeros = valueString.replace(/^0+/, '')
  if (stripLeadingZeros.length <= 6) {
    const paddedString = stripLeadingZeros.padStart(7, '0') // Add leading zeros if needed
    const result = paddedString.slice(0, -6) + '.' + paddedString.slice(-6)
    return result as ERC20DecString
  }
  const result = stripLeadingZeros.slice(0, -6) + '.' + stripLeadingZeros.slice(-6)
  return result as ERC20DecString
}

export const fromWeiToMWeiERC20DecString = (value: ERC20Wei): ERC20DecString => {
  const locale = getLocale()
  const formatter = new Intl.NumberFormat(locale)
  const parts = formatter.formatToParts(12345.6) // Example number to detect delimiters

  const decimalSeparator = parts.find((p) => p.type === 'decimal')?.value || '.'

  const valueString = value.toString()
  const stripLeadingZeros = valueString.replace(/^0+/, '')
  if (stripLeadingZeros.length <= 6) {
    const paddedString = stripLeadingZeros.padStart(7, '0') // Add leading zeros if needed
    const result = paddedString.slice(0, -6) + decimalSeparator + paddedString.slice(-6)
    return result as ERC20DecString
  }
  const result = stripLeadingZeros.slice(0, -6) + decimalSeparator + stripLeadingZeros.slice(-6)
  return result as ERC20DecString
}

export const fromWeitToMWeiNumber = (value: ERC20Wei): number => {
  return Number(fromWeiToMWeiERC20NumberString(value))
}
export const erc20WeiToErc20DecString = (wei: ERC20Wei | number | undefined): ERC20DecString => {
  if (!wei) {
    return '0'
  }
  if (typeof wei === 'number') {
    return fromWeiToMWeiERC20DecString(wei.toLocaleString(getLocale()) as ERC20Wei)
  }
  return fromWeiToMWeiERC20DecString(wei)
}

function getLocale(): string {
  try {
    // if serverside navigator is not defined
    if (!process.env.AWS_LAMBDA_FUNCTION_NAME && navigator?.language) {
      return navigator.language
    }
  } catch (_e) {
    // logOT.error('getLocale', e)
  }
  return 'en-GB'
}
export const toCommaDecString = (value: ERC20DecString | ERC20CommaDecString): ERC20CommaDecString => {
  const locale = getLocale()
  const formatter = new Intl.NumberFormat(locale)
  const parts = formatter.formatToParts(12345.6) // Example number to detect delimiters

  const decimalSeparator = parts.find((p) => p.type === 'decimal')?.value || '.'
  const groupSeparator = parts.find((p) => p.type === 'group')?.value || ','

  // Remove grouping separators and replace decimal separator with `.`
  const noCommaString = value
    .replace(new RegExp(`\\${groupSeparator}`, 'g'), '') // Remove group separators
    .replace(new RegExp(`\\${decimalSeparator}`), '.') // Replace decimal separator

  logOT.debug(
    'toCommaDecString',
    value,
    locale,
    'decimal',
    decimalSeparator,
    'group',
    groupSeparator,
    noCommaString,
    'ToLocaleString',
    Number(noCommaString).toLocaleString(locale)
  )

  return Number(noCommaString).toLocaleString(locale)
}
export const commaDecStringToErcDecString = (value: ERC20CommaDecString): ERC20DecString => {
  const locale = getLocale()
  const formatter = new Intl.NumberFormat(locale)
  const parts = formatter.formatToParts(12345.6) // Example number to detect delimiters

  const decimalSeparator = parts.find((p) => p.type === 'decimal')?.value || '.'
  const groupSeparator = parts.find((p) => p.type === 'group')?.value || ','

  logOT.debug('commaDecStringToErcDecString', value, locale, 'decimal', decimalSeparator, 'group', groupSeparator)

  // Remove grouping separators and replace decimal separator with `.`
  const noCommaString = value.replace(new RegExp(`\\${groupSeparator}`, 'g'), '') // Remove group separators
  // .replace(new RegExp(`\\${decimalSeparator}`), '.') // Replace decimal separator

  logOT.debug(
    'commaDecStringToErcDecString',
    value,
    locale,
    'decimal',
    decimalSeparator,
    'group',
    groupSeparator,
    noCommaString
  )
  return noCommaString as ERC20DecString
}
export const commaDecStringToErc20Wei = (value: ERC20CommaDecString): ERC20Wei => {
  return toErc20Wei(commaDecStringToErcDecString(value))
}

export const commaDecStringToBigNumberWei = (value: ERC20CommaDecString): bigint => {
  return BigInt(toErc20Wei(commaDecStringToErcDecString(value)))
}

export const erc20WeiToNumber = (wei: ERC20Wei | undefined): number => {
  if (!wei) {
    return 0
  }
  logOT.debug('erc20WeiToNumber', wei, typeof wei)
  return fromWeitToMWeiNumber(wei)
}
export const erc20WeiIsSignificant = (wei: ERC20Wei): boolean => {
  return BigInt(wei) >= BigInt(3)
}

export const aprToNumber = (apr: APR): number => {
  return Number(apr) / 100
}

export const aprToDisplay = (apr: APR | undefined | number | string): string => {
  if (!apr) {
    return '0'
  }

  return (Number(apr) / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

export const numberToApr = (apr: number | undefined): APR => {
  if (!apr) {
    return '0'
  }
  return Math.round(apr * 100).toString() as APR
}

export function getRequiredAddress(address: string | undefined): EthAddr {
  if (!address) {
    throw new Error('Address is required')
  }
  try {
    return getAddress(address)
  } catch (e) {
    throw new Error(`Invalid address ${address} ${e}`)
  }
}

export function getOptionalAddress(address: string | undefined): EthAddr | undefined {
  if (!address) {
    return undefined
  }
  try {
    return getAddress(address)
  } catch (e) {
    throw new Error(`Invalid address ${address} ${e}`)
  }
}
